/*
  Maison Bagages : cadeau ajouté au panier sur code promo (bloc mb-cadeau-code). Documenté dans assets/CUSTOM.txt.
  À l'arrivée sur une page et après chaque mise à jour du panier (événement natif cart:updated) :
  - éligible = un code du panier commence par le préfixe ET assez d'articles (hors cadeaux) de la collection ;
  - éligible sans ligne cadeau : ajout de la variante, marquée comme les cadeaux natifs (_is_gift...) ;
  - plus éligible avec ligne cadeau : retrait ; ligne cadeau en quantité > 1 : ramenée à 1 ;
  - ligne retirée par le client alors qu'il était éligible : pas de nouvel ajout pendant la session.
  Collections des articles : même source que le processeur natif (section auto-gifts, extractCartItemCollections).
  Après une modification : rechargement sur la page panier, sinon CartUpdatedEvent avec les sections rendues
  (le tiroir panier se redessine et relance lui-même les cadeaux natifs, coussin compris).
*/
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { extractCartItemCollections } from '@theme/auto-gift-processor';

const SOURCE = 'mb-cadeau-code';

const session = {
  get(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* sans stockage, le refus du client n'est pas mémorisé */
    }
  },
};

class MbCadeauCode extends HTMLElement {
  #timer = null;
  #running = false;
  #again = false;
  #onCartUpdated = null;

  connectedCallback() {
    this.blockId = this.dataset.blockId;
    this.codePrefix = (this.dataset.prefix || '').toUpperCase();
    this.variantId = Number(this.dataset.variantId);
    this.collection = this.dataset.collection;
    this.threshold = Number(this.dataset.threshold) || 1;
    this.badge = (this.dataset.badge || '').trim();
    this.stateKey = `mb:cadeau-code:${this.blockId}`;
    if (!this.blockId || !this.codePrefix || !this.variantId || !this.collection) return;

    this.#onCartUpdated = (event) => {
      if (event.detail?.data?.source === SOURCE) return;
      this.#schedule(500);
    };
    document.addEventListener(ThemeEvents.cartUpdated, this.#onCartUpdated);
    this.#schedule(0);
  }

  disconnectedCallback() {
    if (this.#onCartUpdated) document.removeEventListener(ThemeEvents.cartUpdated, this.#onCartUpdated);
    clearTimeout(this.#timer);
  }

  #schedule(delay) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#run(), delay);
  }

  async #run() {
    if (this.#running) {
      this.#again = true;
      return;
    }
    this.#running = true;
    try {
      await this.#sync();
    } catch (error) {
      console.error('MbCadeauCode', error);
    } finally {
      this.#running = false;
      if (this.#again) {
        this.#again = false;
        this.#schedule(300);
      }
    }
  }

  async #sync() {
    const [cart, collections] = await Promise.all([this.#getJson(`${Theme.routes.cart_url}.js`), this.#getCollections()]);
    if (!cart) return;

    const hasCode = (cart.discount_codes || []).some((discount) => String(discount.code).toUpperCase().startsWith(this.codePrefix));
    let quantity = 0;
    for (const item of cart.items) {
      if (item.properties?._is_gift === 'true') continue;
      if ((collections[item.product_id] || []).includes(this.collection)) quantity += item.quantity;
    }
    const eligible = hasCode && quantity >= this.threshold;
    const giftLines = cart.items.filter((item) => item.properties?._is_gift === 'true' && item.properties?._gift_block_id === this.blockId);
    const paidLine = cart.items.some((item) => item.variant_id === this.variantId && item.properties?._is_gift !== 'true');

    const previous = JSON.parse(session.get(this.stateKey) || '{}');
    let refused = eligible && (previous.refused || (previous.eligible && previous.inCart && giftLines.length === 0));
    let inCart = giftLines.length > 0;
    let result = null;

    if (eligible && !inCart && !refused && !paidLine) {
      const properties = { _is_gift: 'true', _gift_block_id: this.blockId };
      if (this.badge) properties._gift_badge = this.badge;
      result = await this.#post(Theme.routes.cart_add_url, { items: [{ id: this.variantId, quantity: 1, properties }] });
      inCart = Boolean(result);
    } else if (!eligible && inCart) {
      for (const line of giftLines) result = (await this.#post(Theme.routes.cart_change_url, { id: line.key, quantity: 0 })) || result;
      inCart = false;
    } else if (eligible && inCart && giftLines[0].quantity > 1) {
      result = await this.#post(Theme.routes.cart_change_url, { id: giftLines[0].key, quantity: 1 });
    }

    session.set(this.stateKey, JSON.stringify({ eligible, inCart, refused }));
    if (result) this.#refresh(result);
  }

  async #getJson(url) {
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch {
      return null;
    }
  }

  async #getCollections() {
    const data = await this.#getJson(`${Theme.routes.cart_url}?sections=auto-gifts`);
    const html = data?.['auto-gifts'];
    if (!html) return extractCartItemCollections(document);
    return extractCartItemCollections(new DOMParser().parseFromString(html, 'text/html'));
  }

  async #post(url, payload) {
    const sections = ['auto-gifts'];
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.dataset.sectionId) sections.push(drawer.dataset.sectionId);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: sections.join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400 || data.errors) {
        console.warn('MbCadeauCode: opération panier refusée', data);
        return null;
      }
      return data;
    } catch (error) {
      console.error('MbCadeauCode: opération panier en échec', error);
      return null;
    }
  }

  #refresh(data) {
    if (window.location.pathname === Theme.routes.cart_url) {
      window.location.reload();
      return;
    }
    if (!document.querySelector('cart-drawer') || !data.sections) return;
    document.dispatchEvent(new CartUpdatedEvent({}, { source: SOURCE, itemCount: data.item_count, sections: data.sections }));
  }
}

if (!customElements.get('mb-cadeau-code')) {
  customElements.define('mb-cadeau-code', MbCadeauCode);
}
